import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

const VIEWPORT_PAD = 16;
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    // Initial calculations once DOM is ready.
    //
    // Deferred to idle time: submenus are hidden until hover, and #activate
    // recalculates the hovered item before showing it, so nothing here is
    // needed for correctness on first paint. Running it during load competed
    // with more important work.
    onDocumentLoaded(() => {
      const runInitialLayout = () => {
        this.#preloadImages();
        this.#setSubmenuLeftOffsets();

        // Recalc after fonts load (important for "start of word" alignment)
        if (document.fonts?.ready) {
          document.fonts.ready.then(() => this.#setSubmenuLeftOffsets());
        }
      };

      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(runInitialLayout, { timeout: 2000 });
      } else {
        setTimeout(runInitialLayout, 200);
      }
    });

    // Recalc on resize
    window.addEventListener('resize', this.#debouncedRecalcOffsets, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    // Align dropdown to the start of the link text for THIS item
    const li = item.closest('.menu-list__list-item');
    if (li instanceof HTMLElement) {
      this.#ensureSubmenuRendered(li);
      this.#setSubmenuLeftOffsets(li);
    }

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Debounced recalculation for submenu offsets
   */
  #debouncedRecalcOffsets = debounce(() => this.#setSubmenuLeftOffsets(), 100);

  /**
   * Read CSS var (px) from the header-menu element
   * @param {string} name
   * @returns {number}
   */
  #readPxVar(name) {
    const raw = getComputedStyle(this).getPropertyValue(name).trim();
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : 0;
  }

  /**
   * Set per-item submenu left offsets so SUBMENU TEXT aligns with top-level word.
   * Uses a CSS custom property on the <li>: --submenu-left
   * @param {HTMLElement | null} [onlyLi]
   */
  #setSubmenuLeftOffsets = (onlyLi = null) => {
    const items = onlyLi
      ? [onlyLi]
      : Array.from(this.querySelectorAll('.menu-list__list-item:not([slot="overflow"])'));

    if (!items.length) return;

    // Read/write are split into two passes.
    //
    // Previously each iteration read three rects and then wrote --submenu-left
    // on the same element. The write invalidates layout, so the next
    // iteration's getBoundingClientRect() forced a fresh one — nine menu items
    // meant nine synchronous layouts instead of one.
    //
    // getComputedStyle and window.innerWidth are hoisted for the same reason.
    const innerPadX = this.#readPxVar('--submenu-inner-pad-x');
    const viewportWidth = window.innerWidth;

    // Pass 1 — read only.
    const measurements = [];

    for (const li of items) {
      const submenu = li.querySelector('.menu-list__submenu');
      const title = li.querySelector('.menu-list__link-title');

      if (!(submenu instanceof HTMLElement) || !(title instanceof HTMLElement)) continue;
      // Not stamped yet: its width is meaningless. It gets measured on activation.
      if (!onlyLi && submenu.querySelector('template[data-submenu-template]')) continue;

      const liRect = li.getBoundingClientRect();
      const titleRect = title.getBoundingClientRect();
      const submenuRect = submenu.getBoundingClientRect();

      measurements.push({
        li,
        liLeft: liRect.left,
        titleLeft: titleRect.left,
        submenuWidth: submenuRect.width,
      });
    }

    // Pass 2 — write only.
    for (const { li, liLeft, titleLeft, submenuWidth } of measurements) {
      // We want submenu TEXT to align with title text:
      // panelLeft + innerPaddingX == titleTextLeft
      const desiredLeft = titleLeft - liLeft - innerPadX;
      const minLeft = VIEWPORT_PAD - liLeft;
      const maxLeft = viewportWidth - VIEWPORT_PAD - liLeft - submenuWidth;

      const left = clamp(desiredLeft, minLeft, maxLeft);

      li.style.setProperty('--submenu-left', `${Math.round(left)}px`);
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * PERF (Sean, Sep 2026): submenu markup ships inside an inert <template>
   * (see snippets/header-menu.liquid). Clone it into the DOM the first time
   * the item is activated. Idempotent — the template is removed once used.
   * Images inside are stamped without loading="lazy" so they fetch at once;
   * the panel is about to become visible.
   *
   * @param {HTMLElement} li
   */
  #ensureSubmenuRendered(li) {
    const template = li.querySelector('template[data-submenu-template]');
    if (!(template instanceof HTMLTemplateElement)) return;

    const fragment = template.content.cloneNode(true);
    if (fragment instanceof DocumentFragment) {
      fragment.querySelectorAll('img[loading="lazy"]').forEach((img) => img.removeAttribute('loading'));
    }
    template.replaceWith(fragment);
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}